import React from 'react';
import PropTypes from 'prop-types';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Skeleton from '@material-ui/lab/Skeleton';
import { withStyles } from '@material-ui/core/styles';
import { lighten } from '@material-ui/core/styles/colorManipulator';

import Scenario from '../../api/Scenario';
import { athenaBlue, statGreen, athenaLightGrey, statGrey } from '../../globals';
import BaseScenarioDashboardView from '../BaseScenarioDashboardView';
import { autoFormatUnit, roundUnit } from '../../utils';
import { RequiredActionsList } from '../scenarios/ScenarioComponents';
import WhiteTooltip from '../../components/utils/WhiteTooltip';
import Technology from "../../api/Technology";


const styles = theme => ({
  paper: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(4),
  },
  legend: {
    position: 'relative',
    paddingLeft: theme.spacing(3),
    '&::before': {
      content: "''",
      position: 'absolute',
      top: 0,
      left: 0,
      width: theme.spacing(1),
      height: '100%',
      background: 'black',
      borderRadius: theme.shape.borderRadius,
    },
  },
  legendReconciliation: {
    '&::before': {
      background: athenaLightGrey,
    },
  },
  legendOptimization: {
    '&::before': {
      background: athenaBlue,
    },
  },
  legendValidated: {
    '&::before': {
      background: statGreen,
    },
  },
  tooltipList: {
    listStyle: 'none',
    '& li::before': {
      content: '"\\2022"',
      display: 'inline-block',
      fontWeight: 'bold',
      width: 20,
      marginLeft: -20,
      fontSize: 20,
    },
  },
  listReconciliation: {
    '& li::before': {
      color: athenaLightGrey,
    },
  },
  listOptimization: {
    '& li::before': {
      color: athenaBlue,
    },
  },
  listValidated: {
    '& li::before': {
      color: statGreen,
    },
  },
  kpiTitle: {
    paddingLeft: 0,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  kpiRow: {
    '&:nth-child(even)': {
      background: 'none',
    },
    '&:hover, &:active, &:focus': {
      '& $difference': {
        display: 'block',
      },
    },
  },
  kpiCell: {
    width: '100%',
    paddingRight: 0,
  },
  kpiContainer: {
    position: 'relative',
    height: theme.spacing(3),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '&::before': {
      content: "''",
      position: 'absolute',
      top: 'calc(50% - 1px)',
      left: 0,
      width: '100%',
      height: 2,
      background: athenaLightGrey,
    },
  },
  kpi: {
    position: 'absolute',
    width: theme.spacing(3),
    height: theme.spacing(3),
    borderRadius: '50%',
    transform: 'translateX(-50%)',
  },
  baseReconciliationKpi: {
    background: athenaLightGrey,
    color: athenaLightGrey,
    '& $kpiTooltip': {
      background: lighten(athenaLightGrey, 0.8),
    },
  },
  validatedKpi: {
    background: statGreen,
    color: statGreen,
    '& $kpiTooltip': {
      background: lighten(statGreen, 0.8),
    },
  },
  baseOptimizationKpi: {
    background: athenaBlue,
    color: athenaBlue,
    '& $kpiTooltip': {
      background: lighten(athenaBlue, 0.8),
    },
  },
  kpiTooltip: {
    display: 'block',
    position: 'absolute',
    bottom: '150%',
    left: '50%',
    transform: 'translate(-50%)',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontWeight: 'bold',
    zIndex: theme.zIndex.tooltip,
  },
  difference: {
    display: 'none',
    position: 'absolute',
    bottom: `calc(150% + ${theme.spacing(0.5) + theme.typography.fontSize * (theme.typography.body2.lineHeight - 1) / 2}px)`,
    textAlign: 'center',
    fontWeight: 'bold',
    background: `linear-gradient(45deg, #ffffff 40%, ${athenaLightGrey} 40%, ${athenaLightGrey} 50%, #ffffff 50%, #ffffff 90%, ${athenaLightGrey} 90%, ${athenaLightGrey} 100%)`,
    backgroundSize: '7.07px 7.07px',
    lineHeight: 1,
  },
  greyText: {
    color: statGrey,
  },
});


@withStyles(styles)
@observer
class FamilyKeyResults extends React.Component {
  static propTypes = {
    technologyFamily: PropTypes.string.isRequired,
    technologyFamilyLabel: PropTypes.string.isRequired,
    baseReconciliationId: PropTypes.number,
    baseOptimizationId: PropTypes.number,
    validatedId: PropTypes.number,
  };
  static defaultProps = {
    baseReconciliationId: null,
    baseOptimizationId: null,
    validatedId: null,
  };

  @computed get baseReconciliation() {
    const { baseReconciliationId } = this.props;
    if (baseReconciliationId === null) {
      return null;
    }
    const scenario = Scenario.getForId(baseReconciliationId);
    if (!scenario.isLoaded) {
      return null;
    }
    return scenario;
  }

  @computed get baseOptimization() {
    const { baseOptimizationId } = this.props;
    if (baseOptimizationId === null) {
      return null;
    }
    const scenario = Scenario.getForId(baseOptimizationId);
    if (!scenario.isLoaded) {
      return null;
    }
    return scenario;
  }

  @computed get validated() {
    const { validatedId } = this.props;
    if (validatedId === null) {
      return null;
    }
    const scenario = Scenario.getForId(validatedId);
    if (!scenario.isLoaded) {
      return null;
    }
    return scenario;
  }

  get ready() {
    const { baseReconciliationId, baseOptimizationId, validatedId } = this.props;
    if (baseReconciliationId !== null && this.baseReconciliation === null) {
      return false;
    }
    if (baseOptimizationId !== null && this.baseOptimization === null) {
      return false;
    }
    if (validatedId !== null && this.validated === null) {
      return false;
    }
    return true;
  }

  renderLegendItem(title, description, className, tooltipContent) {
    const { classes } = this.props;
    return (
      <Paper className={classes.paper}>
        <div className={[classes.legend, className].join(' ')}>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Typography variant="body1" component="div">{description}</Typography>
        </div>
        <WhiteTooltip>{tooltipContent}</WhiteTooltip>
      </Paper>
    );
  }

  renderLegend() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={200} />;
    }
    const { classes } = this.props;
    return (
      <Grid container spacing={2} wrap="nowrap">
        {
          this.baseReconciliation === null
            ? null
            : (
              <Grid item xs>
                {this.renderLegendItem(
                  'Base reconciliation',
                  <div>Basic supply-demand reconciliation based on certified cycle inputs with <strong>no</strong> moves</div>,
                  classes.legendReconciliation,
                  (
                    <ul className={[classes.tooltipList, classes.listReconciliation].join(' ')}>
                      <li>No product moves nor capacity changes allowed</li>
                      <li>Equivalent of previous 6-box</li>
                      <li>Light optimization (reconstitution of allocation plan)</li>
                    </ul>
                  )
                )}
              </Grid>
            )
        }
        {
          this.baseOptimization === null
            ? null
            : (
              <Grid item xs>
                {this.renderLegendItem(
                  'Base optimization',
                  <div>Best supply-demand optimization <strong>with</strong> moves allowed (same inputs as base reconciliation)</div>,
                  classes.legendOptimization,
                  (
                    <ul className={[classes.tooltipList, classes.listOptimization].join(' ')}>
                      <li>Allows to measure Athena potential for optimization by comparison to base reconciliation</li>
                      <li>Systematically launched by process animator after base reconciliation</li>
                      <li>Same inputs as base reconciliation</li>
                      <li>But with moves allowed (product transfers or capacity changes)</li>
                      <li>Number of moves is set to 5 capacity moves, 5 FG moves & 5 FG transfer moves</li>
                    </ul>
                  )
                )}
              </Grid>
            )
        }
        {
          this.validated === null
            ? null
            : (
              <Grid item xs>
                {this.renderLegendItem(
                  'Preferred / Validated simulation for the cycle',
                  this.validated.description || <em>No description added by users</em>,
                  classes.legendValidated,
                  (
                    <ul className={[classes.tooltipList, classes.listValidated].join(' ')}>
                      <li>
                        Simulation manually selected by the S&OP process animator
                        as the one reflecting at best the vision of M+3-M+18
                        <div className={classes.greyText}>
                          - Can be “base reconciliation”, “base optimization”
                          or a “what if simulation”<br />
                          - Can include or not some product transfers capacity moves
                          recommended by Athena<br />
                          - Associated required actions (listed below) should reflect
                          the action plan that will be implemented by the team
                          to achieve those financial results
                        </div>
                      </li>
                      <li>
                        Before GAC/GPS meetings
                        <div className={classes.greyText}>
                          Preferred simulation for the cycle for decision-making/validation
                        </div>
                      </li>
                      <li>
                        After GAC/GPS meeting
                        <div className={classes.greyText}>
                          Validated simulation for the cycle (frozen vision)
                        </div>
                      </li>
                    </ul>
                  )
                )}
              </Grid>
            )
        }
      </Grid>
    );
  }

  getKpis(kpiName) {
    const kpis = {};
    if (this.baseReconciliation !== null) {
      kpis.baseReconciliation = this.baseReconciliation[kpiName];
    }
    if (this.validated !== null) {
      kpis.validated = this.validated[kpiName];
    }
    if (this.baseOptimization !== null) {
      kpis.baseOptimization = this.baseOptimization[kpiName];
    }

    const kpiValues = Object.values(kpis).map(kpi => roundUnit(kpi.value, kpi.unit));
    const min = Math.min(...kpiValues);
    const max = Math.max(...kpiValues);
    const centeredMin = kpiValues.filter(value => value === min).length > 1;
    const centeredMax = kpiValues.filter(value => value === max).length > 1;
    let minTaken = false;
    let maxTaken = false;
    return Object.fromEntries(Object.entries(kpis).map(([key, kpi]) => {
      kpi = {...kpi};
      kpi.value = roundUnit(kpi.value, kpi.unit);
      if (kpiValues.length < 2) {
        kpi.position = 0.5;
      } else if (kpi.value === min && !minTaken) {
        kpi.position = centeredMin ? 0.35 : 0.05;
        minTaken = true;
      } else if (kpi.value === max && !maxTaken) {
        kpi.position = centeredMax ? 0.65 : 0.95;
        maxTaken = true;
      } else if (centeredMin || centeredMax) {
        kpi.position = 0.5;
      } else {
        kpi.position = 0.2 + 0.6 * (kpi.value - min) / (max - min);
      }
      return [key, kpi];
    }));
  }

  renderDifference(first, second) {
    const { classes } = this.props;
    return (
      <div
        className={classes.difference}
        style={{
          left: `${100 * first.position}%`,
          width: `${100 * (second.position - first.position)}%`,
        }}>
        {(second.value > first.value ? '+' : '') + autoFormatUnit(
          second.value - first.value,
          first.unit,
        )}
      </div>
    );
  };

  renderKpi(kpiName, title) {
    const { classes } = this.props;
    const kpis = this.getKpis(kpiName);
    const sortedKpis = (
      Object.values(kpis).sort((a, b) => a.position - b.position)
    );
    let differences = null;
    if (sortedKpis.length === 3) {
      const [first, second, third] = sortedKpis;
      differences = (
        <>
          {this.renderDifference(first, second)}
          {this.renderDifference(second, third)}
        </>
      );
    } else if (sortedKpis.length === 2) {
      const [first, second] = sortedKpis;
      differences = this.renderDifference(first, second);
    }
    return (
      <TableRow className={classes.kpiRow}>
        <TableCell className={classes.kpiTitle}>{title}</TableCell>
        <TableCell className={classes.kpiCell}>
          <div className={classes.kpiContainer}>
            {differences}
            {
              Object.entries(kpis).map(([key, kpi]) =>
                <div
                  key={key}
                  className={[classes.kpi, classes[`${key}Kpi`]].join(' ')}
                  style={{left: `${100 * kpi.position}%`}}
                >
                  <div className={classes.kpiTooltip}>
                    {autoFormatUnit(kpi.value, kpi.unit)}
                  </div>
                </div>
              )
            }
          </div>
        </TableCell>
      </TableRow>
    );
  }

  renderKpis() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={400} />;
    }
    const { classes } = this.props;
    return (
      <Paper className={classes.paper}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="h6">High level comparison</Typography>
          </Grid>
          <Grid item>
            <Table>
              <TableBody>
                {this.renderKpi('sales_kpi', 'Yearly served demand')}
                {this.renderKpi('costs_kpi', 'Yearly costs')}
                {this.renderKpi('cash_locked_kpi', 'AVG cost of cash')}
                {this.renderKpi('availability_fg_kpi', 'Sales Coverage')}
                {this.renderKpi('capacity_fg_kpi', 'FG capacity utilization')}
                {this.renderKpi('capacity_sfg_kpi', 'SFG capacity utilization')}
                {this.renderKpi('stock_fg_kpi', 'AVG FG stock value')}
                {this.renderKpi('stock_sfg_kpi', 'AVG SFG stock value')}
                {this.renderKpi('stock_below_minimum', 'Stock below minimum')}
                {this.renderKpi('stock_above_maximum', 'Stock above maximum')}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  renderRequiredActions() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={150} />;
    }
    if (this.validated === null) {
      return null;
    }
    return (
      <RequiredActionsList
        title="Required actions for validated scenario"
        items={[...this.validated.required_actions]}
      />
    );
  }

  render() {
    const { technologyFamilyLabel } = this.props;
    return (
      <Grid container direction="column" spacing={2}>
        <Grid item><Typography variant="h4">{technologyFamilyLabel} technologies</Typography></Grid>
        <Grid item>{this.renderLegend()}</Grid>
        <Grid item>{this.renderKpis()}</Grid>
        {
          this.validated === null
            ? null
            : <Grid item>{this.renderRequiredActions()}</Grid>
        }
      </Grid>
    );
  }
}


@withStyles(styles)
@observer
class CycleKeyResultsView extends BaseScenarioDashboardView {
  static apiSubPath = '/cycle_key_results/';

  get ready() {
    return super.ready && Technology.fieldsData !== null;
  }

  render() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={600} />;
    }
    if (Object.keys(this.data).length === 0) {
      return 'No base reconciliation, base optimization, preferred or validated scenario found for this cycle.';
    }
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        {[
          { value: 'all', display_name: 'All' },
          ...Technology.fieldsData.family.choices,
        ].map(({ value: family, display_name: familyLabel }) => {
          if (!(family in this.data)) {
            return null;
          }
          const { base_reconciliation, base_optimization, validated } = this.data[family];
          return (
            <Grid item>
              <FamilyKeyResults
                key={family}
                technologyFamily={family}
                technologyFamilyLabel={familyLabel}
                baseReconciliationId={base_reconciliation}
                baseOptimizationId={base_optimization}
                validatedId={validated}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  }
}


export default CycleKeyResultsView;
